/**
 * The staged loading status shown after ENTER and on every start: "Loading files… 42%",
 * "Applying the 1.14d patch…", "Loading game data…", with a compact controls card to read while
 * waiting. Keys come from the game's Configure Controls bindings (game/controls.ts); the mouse
 * and the few keys that page doesn't list are added here. Plain DOM in the import screen's style:
 * no game art exists until the archives are open.
 */
import { CONTROL_BINDINGS } from '../game/controls';
import './import.css';

/** Readable names for the binding labels (the string table isn't loaded yet). */
const NAMES: Record<string, string> = {
  CfgCharacter: 'Character',
  CfgInventory: 'Inventory',
  CfgSkillTree: 'Skill tree',
  CfgQuestLog: 'Quests',
  CfgHelp: 'Help',
  CfgParty: 'Party',
  Cfghireling: 'Hireling',
  CfgRunLock: 'Run / walk',
  CfgShowItems: 'Show items on the ground',
  CfgAutoMap: 'Automap',
  CfgChat: 'Chat',
};

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const keysOf = (label: string) => CONTROL_BINDINGS.find((b) => b.label === label)?.keys.split(',')[0].trim() ?? null;

/** [keys, action] rows for the controls card. */
export function controlRows(mac = isMac()): [string, string][] {
  const rows: [string, string][] = [
    ['Left click', 'Move, attack, talk, pick up'],
    ['Right click', 'Use the right-hand skill'],
    ['Shift + click', 'Attack while standing still'],
  ];
  const key = (label: string) => {
    const k = keysOf(label);
    if (k) rows.push([k === 'Alt' && mac ? 'Option' : k, NAMES[label] ?? label]);
  };
  key('CfgRunLock');
  key('CfgShowItems');
  key('CfgAutoMap');
  const panels = ['CfgCharacter', 'CfgInventory', 'CfgSkillTree', 'CfgQuestLog', 'CfgParty', 'Cfghireling', 'CfgHelp']
    .map((l) => [keysOf(l), NAMES[l]] as const)
    .filter(([k]) => k);
  if (panels.length) rows.push([panels.map(([k]) => k).join(' '), panels.map(([, n]) => n).join(', ')]);
  const skills = CONTROL_BINDINGS.filter((b) => /^CfgSkill\d$/.test(b.label)).map((b) => b.keys);
  if (skills.length) rows.push([`${skills[0]}–${skills.at(-1)}`, 'Skill hotkeys']);
  const belt = CONTROL_BINDINGS.filter((b) => /^CfgBelt\d$/.test(b.label)).map((b) => b.keys);
  if (belt.length) rows.push([`${belt[0]}–${belt.at(-1)}`, 'Drink from the belt']);
  key('CfgChat');
  rows.push(['Esc', 'Game menu']);
  return rows;
}

export interface Loading {
  /** A stage line; `pct` (0–100) fills the bar, without it the bar pulses. */
  stage(text: string, pct?: number): void;
  readonly el: HTMLElement;
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

/** The loading block's markup (stage line, bar, controls card). */
export function loadingHtml(): string {
  return `
    <div class="loading">
      <div class="stage" role="status" aria-live="polite">Starting…</div>
      <div class="progress" aria-hidden="true"><div></div></div>
      <section class="controls" aria-label="Controls">
        <h2>Controls</h2>
        <dl>${controlRows().map(([k, a]) => `<dt>${esc(k)}</dt><dd>${esc(a)}</dd>`).join('')}</dl>
      </section>
    </div>`;
}

function bind(el: HTMLElement): Loading {
  const stage = el.querySelector<HTMLElement>('.stage')!;
  const bar = el.querySelector<HTMLElement>('.progress')!;
  const fill = el.querySelector<HTMLElement>('.progress > div')!;
  return {
    el,
    stage(text, pct) {
      stage.textContent = pct === undefined ? text : `${text} ${Math.floor(pct)}%`;
      bar.classList.toggle('busy', pct === undefined);
      fill.style.width = pct === undefined ? '' : `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`;
    },
  };
}

/** Reuses the loading block already on screen (the import screen's) or shows a new card. */
export function loadingPanel(root: HTMLElement): Loading {
  const existing = root.querySelector<HTMLElement>('.loading');
  if (existing) return bind(existing);
  root.innerHTML = `<div class="import"><div class="card loading-card">${loadingHtml()}</div></div>`;
  return bind(root.querySelector<HTMLElement>('.loading')!);
}
